import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Share2 } from 'lucide-react';
import { FaXTwitter, FaLinkedin } from 'react-icons/fa6';
import { useData } from '../context/DataContext';
const patoMascot = '/images/patoyazılım-maskot.webp';

import SEO from '../components/SEO';

const BlogPostDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { blogPosts } = useData();

    const post = blogPosts.find(p => p.slug === slug);

    useEffect(() => {
        if (!post) {
            navigate('/blog');
        }
        window.scrollTo(0, 0);
    }, [post, navigate]);

    if (!post) return null;

    return (
        <div className="w-full bg-white min-h-screen">
            <SEO
                title={post.title}
                description={post.excerpt}
                link={`/blog/${post.slug}`}
                image={post.image}
                type="article"
            />
            {/* Article Header */}
            <section className="pt-32 pb-12 bg-[#353589]">
                <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
                    <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-400 mb-6">
                        <span className="bg-brand-blue/20 text-brand-blue px-3 py-1 rounded-full font-bold border border-brand-blue/20">
                            {post.category}
                        </span>
                        <span className="flex items-center">
                            <Calendar size={16} className="mr-2" /> {post.date}
                        </span>
                        {post.readTime && (
                            <span className="flex items-center">
                                <Clock size={16} className="mr-2" /> {post.readTime} okuma
                            </span>
                        )}
                    </div>

                    <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                        {post.title}
                    </h1>
                </div>
            </section>

            {/* Content & Sidebar Layout */}
            <section className="py-12 md:py-20">
                <div className="container mx-auto px-4 md:px-8 max-w-7xl">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                        {/* Main Content Column */}
                        <div className="lg:col-span-8">
                            <div
                                className="prose prose-lg prose-blue max-w-none text-gray-700 blog-content"
                                dangerouslySetInnerHTML={{ __html: post.content || `<p>${post.excerpt}</p><p>Detaylı içerik hazırlanıyor...</p>` }}
                            />

                            {/* Author & Share Bottom */}
                            <div className="mt-16 pt-8 border-t border-gray-100">
                                <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-16 h-16 bg-gray-100 rounded-full overflow-hidden">
                                            <img src={patoMascot} alt="Makale Yazarı" className="w-full h-full object-cover" />
                                        </div>
                                        <div>
                                            <div className="font-bold text-gray-900 text-lg">{post.author || 'Pato Yazılım Ekibi'}</div>
                                            <div className="text-sm text-gray-500">Yazar</div>
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        <a
                                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-black hover:text-white transition-colors"
                                            title="X'te Paylaş"
                                        >
                                            <FaXTwitter size={18} />
                                        </a>
                                        <a
                                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-[#0077b5] hover:text-white transition-colors"
                                            title="LinkedIn'de Paylaş"
                                        >
                                            <FaLinkedin size={18} />
                                        </a>
                                        <button
                                            onClick={() => {
                                                if (navigator.share) {
                                                    navigator.share({
                                                        title: post.title,
                                                        text: post.excerpt,
                                                        url: window.location.href,
                                                    }).catch(console.error);
                                                } else {
                                                    navigator.clipboard.writeText(window.location.href);
                                                    alert('Link kopyalandı!');
                                                }
                                            }}
                                            className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-brand-blue hover:text-white transition-colors"
                                            title="Paylaş"
                                        >
                                            <Share2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Tags & Back Button */}
                            <div className="mt-8 pt-8 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-6">
                                <div className="flex flex-wrap gap-2">
                                    <span className="text-gray-500 font-medium mr-2 flex items-center">Etiketler:</span>
                                    {post.category.split(' / ').map((tag, idx) => (
                                        <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-md text-sm hover:bg-gray-200 cursor-pointer transition-colors">
                                            {tag.trim()}
                                        </span>
                                    ))}
                                </div>

                                <Link to="/blog" className="inline-flex items-center text-brand-blue hover:text-brand-dark transition-colors font-medium">
                                    <ArrowLeft size={20} className="mr-2" /> Blog'a Dön
                                </Link>
                            </div>
                        </div>

                        {/* Sidebar Column */}
                        <div className="lg:col-span-4">
                            <div className="sticky top-32">
                                <h3 className="text-xl font-bold text-brand-dark mb-6 pb-2 border-b border-gray-100">İlginizi Çekebilir</h3>
                                <div className="flex flex-col gap-6">
                                    {blogPosts.filter(p => p.id !== post.id).slice(0, 3).map(related => (
                                        <div key={related.id} className="group cursor-pointer flex gap-4 items-start" onClick={() => navigate(`/blog/${related.slug}`)}>
                                            <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                                                <img src={related.image} alt={related.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                                            </div>
                                            <div>
                                                <div className="text-xs text-brand-blue font-bold mb-1">{related.category}</div>
                                                <h4 className="text-sm font-bold text-brand-dark group-hover:text-brand-blue transition-colors line-clamp-2">{related.title}</h4>
                                                <span className="text-xs text-gray-400 mt-1 block">{related.date}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </div>
    );
};

export default BlogPostDetail;
